import React, { useMemo } from 'react';
import {
    ProductsToProducePageQuery,
    useProductsToProducePageQuery,
} from '../../../../services';
import { DPage, DPageBody, DPageLoader } from '../../../dum';
import { ProductsToProduceByProduct } from './blocks/product-to-produce-by-product';
import { ProductsToProduceByWidthCalibreCategory } from './blocks/product-to-produce-by-width-calibre-category';
import { ProductsToProduceByProductCategory } from './blocks/product-to-produce-by-product-category';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { useBoolean } from '../../../../helpers';
import { isInventoryTracked } from '../../../../services/entities';

type OptimizedRequestProducts = (NonNullable<
    NonNullable<ProductsToProducePageQuery['getOptimizedRequestProducts']>
>[number] & {
    product: ProductsToProducePageQuery['getProductsInventory'][number]['product'];
})[];

export type ProductToProduce = {
    product_id: number | null;
    product: ProductsToProducePageQuery['getProductsInventory'][number]['product'];
    // False for pellet/lavado, whose consumption is never captured. When false
    // the inventory balance is not real, so "por producir" is left at 0 rather
    // than computed as requested − delivered − 0 (which would treat an unknown
    // stock as empty and inflate the requirement).
    inventoryTracked: boolean;
    kilos_in_inventory: number;
    kilos_to_produce: number;
    kilos_requested: number;
    kilos_sold: number;
    groups_in_inventory: number;
    groups_to_produce: number;
    groups_requested: number;
    groups_sold: number;
    optimized_request_products: OptimizedRequestProducts;
};

export function ProductsToProduce() {
    const { data, loading } = useProductsToProducePageQuery();

    const [isGroups, setIsGroups] = useBoolean({
        KEY: ProductsToProduce.name + 'IsGroups',
        defaultValue: false,
    });

    const [includePending, setIncludePending] = useBoolean({
        KEY: ProductsToProduce.name + 'IncludePending',
        defaultValue: true,
    });

    const productsToProduce = useMemo<ProductToProduce[]>(() => {
        const temporaryProductsToProduce: ProductToProduce[] = [];

        if (data?.getProductsInventory && data?.getOptimizedRequestProducts) {
            data?.getProductsInventory.forEach((productInventory) => {
                const inventoryTracked = isInventoryTracked(
                    productInventory.product?.order_production_type_id ?? null,
                );
                const productToProduce: ProductToProduce = {
                    product: productInventory.product,
                    product_id: productInventory.product?.id || null,
                    inventoryTracked,
                    kilos_in_inventory: productInventory.kilos || 0,
                    kilos_requested: 0,
                    kilos_sold: 0,
                    // Seed with the negative of stock on hand only when that
                    // stock is a real number. For an untracked product the
                    // balance is unknown, so treating it as 0 here would let a
                    // missing inventory drive the requirement up.
                    kilos_to_produce:
                        inventoryTracked && productInventory.kilos
                            ? -productInventory.kilos
                            : 0,
                    groups_in_inventory: productInventory.groups || 0,
                    groups_requested: 0,
                    groups_to_produce:
                        inventoryTracked && productInventory.groups
                            ? -productInventory.groups
                            : 0,
                    groups_sold: 0,
                    optimized_request_products: [],
                };

                data?.getOptimizedRequestProducts.forEach((requestProduct) => {
                    if (
                        requestProduct.product_id ===
                        productInventory.product?.id
                    ) {
                        productToProduce.kilos_requested +=
                            requestProduct.order_request_kilos || 0;
                        productToProduce.groups_requested +=
                            requestProduct.order_request_groups || 0;
                        productToProduce.optimized_request_products.push({
                            ...requestProduct,
                            product: productInventory.product,
                        });

                        productToProduce.kilos_sold +=
                            requestProduct.order_sale_delivered_kilos || 0;
                        productToProduce.groups_sold +=
                            requestProduct.order_sale_delivered_groups || 0;

                        // "Por producir" = requested − delivered − inventory.
                        // Only meaningful when inventory is tracked; for
                        // pellet/lavado we cannot say how much to make, so the
                        // figure stays 0 and the row is marked untracked.
                        if (inventoryTracked) {
                            productToProduce.groups_to_produce +=
                                (requestProduct.order_request_groups || 0) -
                                (requestProduct.order_sale_delivered_groups ||
                                    0);

                            productToProduce.kilos_to_produce +=
                                (requestProduct.order_request_kilos || 0) -
                                (requestProduct.order_sale_delivered_kilos || 0);
                        }

                        if (
                            requestProduct.order_request_status_id === 1 &&
                            !includePending
                        ) {
                            productToProduce.kilos_requested -=
                                requestProduct.order_request_kilos || 0;
                            productToProduce.groups_requested -=
                                requestProduct.order_request_groups || 0;

                            if (inventoryTracked) {
                                productToProduce.groups_to_produce -=
                                    requestProduct.order_request_groups || 0;
                                productToProduce.kilos_to_produce -=
                                    requestProduct.order_request_kilos || 0;
                            }
                        }
                    }
                });

                temporaryProductsToProduce.push(productToProduce);
            });
        }

        return temporaryProductsToProduce;
    }, [data, includePending]);

    return (
        <DPage>
            <DPageBody disableGutters>
                {/* Controls row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 2 }}>
                    <FormControl>
                        <FormControlLabel
                            labelPlacement={'end'}
                            control={
                                <Switch
                                    onChange={() => {
                                        setIsGroups(!isGroups);
                                    }}
                                    checked={isGroups}
                                />
                            }
                            label={isGroups ? 'Kilos' : 'Bultos'}
                        />
                    </FormControl>
                    <FormControl>
                        <FormControlLabel
                            labelPlacement={'end'}
                            control={
                                <Checkbox
                                    onChange={() => {
                                        setIncludePending(!includePending);
                                    }}
                                    checked={includePending}
                                />
                            }
                            label={'Incluir pedidos pendientes'}
                        />
                    </FormControl>
                </Box>
                {loading ? (
                    <DPageLoader />
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0, overflow: 'auto', px: 2 }}>
                        <ProductsToProduceByProduct
                            productsToProduce={productsToProduce}
                            useGroups={!isGroups}
                        />
                        <ProductsToProduceByWidthCalibreCategory
                            productsToProduce={productsToProduce}
                            useGroups={!isGroups}
                        />
                        <ProductsToProduceByProductCategory
                            productsToProduce={productsToProduce}
                            useGroups={!isGroups}
                        />
                    </Box>
                )}
            </DPageBody>
        </DPage>
    );
}
